/**
 * IntakeBundleDocNumberBackfillCard
 * ──────────────────────────────────
 * Admin → Diagnostics — one-click backfill for intake-bundle notes
 * that were created before the Mar 2026 fix and are missing a
 * ``doc_number`` (DOC-XXXXXX) friendly ID.
 *
 * Backend:
 *   POST /api/admin/backfill-intake-bundle-doc-numbers?dry_run=true|false
 *
 * Flow:
 *   1. "Scan" (dry_run=true)  → shows candidate count
 *   2. "Apply Backfill" (dry_run=false) → stamps each candidate
 *   3. Idempotent — subsequent scans return 0 once done.
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Search, RefreshCw, FileText, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const IntakeBundleDocNumberBackfillCard = () => {
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  const scan = useCallback(async () => {
    setScanning(true);
    setApplyResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/backfill-intake-bundle-doc-numbers?dry_run=true`,
      );
      setScanResult(data);
      if ((data.candidates || 0) === 0) {
        toast.success('All intake-bundle notes already have a DOC-ID. Nothing to backfill.');
      } else {
        toast.info(`Found ${data.candidates} bundle note(s) missing a DOC-ID.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  }, []);

  const apply = useCallback(async () => {
    if (!scanResult || !scanResult.candidates) return;
    const ok = window.confirm(
      `Stamp a new DOC-ID on ${scanResult.candidates} intake-bundle note(s)?\n\n`
      + 'This is idempotent — running it a second time is a no-op.',
    );
    if (!ok) return;
    setApplying(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/backfill-intake-bundle-doc-numbers?dry_run=false`,
      );
      setApplyResult(data);
      toast.success(`Stamped ${data.updated} DOC-ID(s).${data.failed ? ` ${data.failed} failed.` : ''}`);
      // Refresh scan so the count reflects the new state.
      setScanResult(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Backfill failed');
    } finally {
      setApplying(false);
    }
  }, [scanResult]);

  return (
    <Card data-testid="intake-bundle-doc-id-backfill-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4 text-blue-600" />
          Intake-Bundle DOC-ID Backfill
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Stamps a friendly <span className="font-mono">DOC-XXXXXX</span> ID onto every
          intake-bundle note created before the Mar 2026 fix.  Run once — idempotent.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={scan}
            disabled={scanning || applying}
            data-testid="intake-bundle-doc-id-scan-btn"
          >
            {scanning ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Search className="h-3.5 w-3.5 mr-1" />}
            {scanning ? 'Scanning…' : 'Scan for missing DOC-IDs'}
          </Button>
          {scanResult && scanResult.candidates > 0 && (
            <Button
              size="sm"
              onClick={apply}
              disabled={applying}
              data-testid="intake-bundle-doc-id-apply-btn"
            >
              {applying ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
              {applying ? 'Applying…' : `Apply backfill (${scanResult.candidates})`}
            </Button>
          )}
        </div>

        {scanResult && scanResult.candidates === 0 && !applyResult && (
          <div
            className="flex items-start gap-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
            data-testid="intake-bundle-doc-id-all-clean"
          >
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
            <span>All intake-bundle notes already have a DOC-ID.  Nothing to do.</span>
          </div>
        )}

        {scanResult && scanResult.candidates > 0 && !applyResult && (
          <div
            className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            data-testid="intake-bundle-doc-id-scan-result"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              <strong>{scanResult.candidates}</strong> bundle note{scanResult.candidates === 1 ? '' : 's'} missing a DOC-ID.
              Click <em>Apply backfill</em> to stamp them.
            </span>
          </div>
        )}

        {applyResult && (
          <div
            className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 space-y-1"
            data-testid="intake-bundle-doc-id-apply-result"
          >
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              <span>
                Stamped <strong>{applyResult.updated}</strong> of {applyResult.candidates} note(s).
                {applyResult.failed > 0 && (
                  <span className="text-rose-700 ml-1">{applyResult.failed} failed.</span>
                )}
              </span>
            </div>
            {applyResult.fail_samples && applyResult.fail_samples.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-rose-700">View failures</summary>
                <ul className="mt-1 ml-4 list-disc space-y-0.5">
                  {applyResult.fail_samples.map((f, i) => (
                    <li key={i}>
                      <span className="font-mono">{f.id}</span> — {f.error}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default IntakeBundleDocNumberBackfillCard;
